import { useEffect, useState } from 'react';

/**
 * Compact offline indicator. When navigator.onLine flips false (browser-
 * level network drop), surface a fixed-top stripe so the rep knows the
 * stuck loader / failed brief is the network, not the app. Auto-hides
 * the moment connectivity returns.
 *
 * navigator.onLine alone is loose (some browsers report online when a
 * captive portal is hijacking traffic), but the online/offline events
 * are reliable for the hard wifi / cell drops field reps actually hit —
 * which is the case worth surfacing. Renders nothing when online so the
 * UI stays clean.
 */
export function OfflineBanner() {
  const initial =
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
      ? navigator.onLine
      : true;
  const [online, setOnline] = useState(initial);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: '8px 16px calc(8px + env(safe-area-inset-top))',
        background: '#7F1D1D',
        color: '#FFE2E2',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 12.5,
        fontWeight: 700,
        textAlign: 'center',
        letterSpacing: 0.3,
        boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
      }}
    >
      You're offline · PREP can't run live research until you're back online
    </div>
  );
}
